import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Upload,
  X,
  AlertCircle,
  CheckCircle,
  Loader,
  ChevronLeft
} from 'lucide-react'
import { supabase, uploadFile, getFileUrl } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { SERVICE_CATEGORIES, BRAND_SPECIALTIES, REGIONS } from '../../lib/serviceProviders'

export default function MyListing() {
  const navigate = useNavigate()
  const { user, profile } = useAuth()

  const [provider, setProvider] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    phone: '',
    website: '',
    price_range: '$$',
    category: '',
    location: '',
    region: '',
    logo_url: '',
    specialties: [],
    services: []
  })

  const [photos, setPhotos] = useState([])
  const [uploading, setUploading] = useState(false)
  const [newSpecialty, setNewSpecialty] = useState('')
  const [newService, setNewService] = useState('')

  // Fetch provider listing
  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }
    fetchProviderListing()
  }, [user])

  const fetchProviderListing = async () => {
    try {
      setLoading(true)
      setError('')

      // Try finding by user ID first, then by email
      let { data, error: fetchError } = await supabase
        .from('service_providers')
        .select('*')
        .eq('claimed_by', user.id)
        .maybeSingle()

      // If not found by user ID, try matching by claimed_by_email
      if (!data && user.email) {
        const { data: emailMatch, error: emailError } = await supabase
          .from('service_providers')
          .select('*')
          .eq('claimed_by_email', user.email)
          .maybeSingle()

        if (emailMatch && !emailError) {
          data = emailMatch
          fetchError = null
          // Link the user ID for future lookups
          await supabase
            .from('service_providers')
            .update({ claimed_by: user.id })
            .eq('id', emailMatch.id)
        }
      }

      if (fetchError) throw fetchError

      if (data) {
        setProvider(data)
        setFormData({
          name: data.name || '',
          description: data.description || '',
          phone: data.phone || '',
          website: data.website || '',
          price_range: data.price_range || '$$',
          category: data.category || '',
          location: data.location || '',
          region: data.region || '',
          logo_url: data.logo_url || '',
          specialties: Array.isArray(data.specialties) ? data.specialties : [],
          services: Array.isArray(data.services) ? data.services : []
        })

        // Load photos if they exist
        if (data.photos && Array.isArray(data.photos)) {
          setPhotos(data.photos.map(p => ({ url: p, temp: false })))
        }
      } else {
        setError('No claimed listing found. Please claim a business listing first.')
      }
    } catch (err) {
      console.error('Error fetching provider:', err)
      setError('Error loading your listing. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleFieldChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleAddSpecialty = () => {
    if (newSpecialty.trim() && !formData.specialties.includes(newSpecialty.trim())) {
      setFormData(prev => ({
        ...prev,
        specialties: [...prev.specialties, newSpecialty.trim()]
      }))
      setNewSpecialty('')
    }
  }

  const handleRemoveSpecialty = (specialty) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s !== specialty)
    }))
  }

  const handleAddService = () => {
    if (newService.trim() && !formData.services.includes(newService.trim())) {
      setFormData(prev => ({
        ...prev,
        services: [...prev.services, newService.trim()]
      }))
      setNewService('')
    }
  }

  const handleRemoveService = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter(s => s !== service)
    }))
  }

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !provider) return

    try {
      setUploading(true)
      setError('')

      const fileExt = file.name.split('.').pop()
      const fileName = `${provider.id}/logo.${fileExt}`

      const { error: uploadError } = await uploadFile('provider-images', fileName, file)
      if (uploadError) throw uploadError

      const url = getFileUrl('provider-images', fileName)
      setFormData(prev => ({
        ...prev,
        logo_url: url
      }))
    } catch (err) {
      console.error('Error uploading logo:', err)
      setError('Failed to upload logo. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handlePhotoUpload = async (e) => {
    const files = e.target.files
    if (!files || !provider) return

    // Max 6 photos
    if (photos.length + files.length > 6) {
      setError('You can upload a maximum of 6 photos.')
      return
    }

    try {
      setUploading(true)
      setError('')

      const uploadedPhotos = []

      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const fileExt = file.name.split('.').pop()
        const fileName = `${provider.id}/photo-${Date.now()}-${i}.${fileExt}`

        const { error: uploadError } = await uploadFile('provider-images', fileName, file)
        if (uploadError) throw uploadError

        const url = getFileUrl('provider-images', fileName)
        uploadedPhotos.push({ url, temp: true })
      }

      setPhotos(prev => [...prev, ...uploadedPhotos])
    } catch (err) {
      console.error('Error uploading photos:', err)
      setError('Failed to upload photos. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleRemovePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setError('')
    setSuccessMessage('')

    if (!formData.name.trim()) {
      setError('Business name is required')
      return
    }
    if (!formData.description.trim()) {
      setError('Business description is required')
      return
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required')
      return
    }
    if (!formData.category) {
      setError('Please select a service category')
      return
    }
    if (!formData.location.trim()) {
      setError('Business location is required')
      return
    }

    try {
      setSaving(true)

      // Extract URLs from photos
      const photoUrls = photos.map(p => p.url)

      const updateData = {
        name: formData.name,
        description: formData.description,
        phone: formData.phone,
        website: formData.website || null,
        price_range: formData.price_range,
        category: formData.category,
        location: formData.location,
        region: formData.region || null,
        logo_url: formData.logo_url || null,
        specialties: formData.specialties.length > 0 ? formData.specialties : null,
        services: formData.services.length > 0 ? formData.services : null,
        photos: photoUrls.length > 0 ? photoUrls : null
      }

      const { error: updateError } = await supabase
        .from('service_providers')
        .update(updateData)
        .eq('id', provider.id)

      if (updateError) throw updateError

      setSuccess(true)
      setSuccessMessage('Your listing has been updated successfully!')

      // Clear success message after 4 seconds
      setTimeout(() => {
        setSuccess(false)
        setSuccessMessage('')
      }, 4000)
    } catch (err) {
      console.error('Error saving listing:', err)
      setError(err.message || 'Failed to save your listing. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader size={32} className="animate-spin text-rdc-orange mx-auto mb-3" />
          <p className="text-rdc-taupe">Loading your listing...</p>
        </div>
      </div>
    )
  }

  if (!provider) {
    return (
      <div className="animate-fade-in max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/garage')}
          className="flex items-center gap-2 text-rdc-orange hover:text-rdc-orange/80 mb-8 font-medium"
        >
          <ChevronLeft size={18} />
          Back to Garage
        </button>

        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-50 flex items-center justify-center">
            <AlertCircle size={32} className="text-red-600" />
          </div>

          <h1 className="page-title mb-3">No Listing Found</h1>

          <p className="text-body text-rdc-charcoal mb-8 max-w-lg mx-auto">
            You haven't claimed a service provider listing yet. Please claim a listing first to edit it.
          </p>

          <a href="/specialists/claim" className="btn-primary inline-block">
            Claim a Listing
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="animate-fade-in max-w-4xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-rdc-orange hover:text-rdc-orange/80 mb-8 font-medium"
      >
        <ChevronLeft size={18} />
        Go Back
      </button>

      {/* Header */}
      <div className="mb-8">
        <h1 className="page-title">My Listing</h1>
        <p className="text-body mt-2">Edit your service provider listing</p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-rdc-forest/5 border border-rdc-forest/30 rounded-lg flex items-start gap-3 animate-fade-in">
          <CheckCircle size={18} className="text-rdc-forest flex-shrink-0 mt-0.5" />
          <p className="text-sm text-rdc-forest font-medium">{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-8">
        {/* Business Info Card */}
        <div className="card p-8">
          <h2 className="section-title mb-6 flex items-center gap-2">
            <span className="text-lg">📋</span>
            Business Information
          </h2>

          <div className="space-y-6">
            {/* Business Name */}
            <div>
              <label className="form-label">Business Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFieldChange}
                className="input-field"
              />
            </div>

            {/* Description */}
            <div>
              <label className="form-label">Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFieldChange}
                rows="4"
                className="input-field resize-none"
              />
            </div>

            {/* Phone & Website */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFieldChange}
                  className="input-field"
                />
              </div>

              <div>
                <label className="form-label">Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleFieldChange}
                  placeholder="https://..."
                  className="input-field"
                />
              </div>
            </div>

            {/* Price Range */}
            <div>
              <label className="form-label">Price Range</label>
              <select
                name="price_range"
                value={formData.price_range}
                onChange={handleFieldChange}
                className="input-field"
              >
                <option value="$">$ - Budget-Friendly</option>
                <option value="$$">$$ - Moderate</option>
                <option value="$$$">$$$ - Premium</option>
                <option value="$$$$">$$$$ - Luxury</option>
              </select>
            </div>
          </div>
        </div>

        {/* Services & Specialties Card */}
        <div className="card p-8">
          <h2 className="section-title mb-6 flex items-center gap-2">
            <span className="text-lg">🔧</span>
            Services & Specialties
          </h2>

          <div className="space-y-6">
            {/* Category */}
            <div>
              <label className="form-label">Service Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleFieldChange}
                className="input-field"
              >
                <option value="">Select a category</option>
                {SERVICE_CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>

            {/* Specialties */}
            <div>
              <label className="form-label">Brand Specialties</label>
              <div className="flex gap-2 mb-3">
                <select
                  value={newSpecialty}
                  onChange={(e) => setNewSpecialty(e.target.value)}
                  className="input-field flex-1"
                >
                  <option value="">Select or type specialty</option>
                  {BRAND_SPECIALTIES.filter(b => !formData.specialties.includes(b)).map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleAddSpecialty}
                  className="btn-secondary px-4"
                >
                  Add
                </button>
              </div>

              {formData.specialties.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.specialties.map(specialty => (
                    <div
                      key={specialty}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-rdc-cream rounded-full text-sm text-rdc-charcoal"
                    >
                      <span>{specialty}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveSpecialty(specialty)}
                        className="text-rdc-taupe hover:text-rdc-charcoal"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Services */}
            <div>
              <label className="form-label">Services Offered</label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newService}
                  onChange={(e) => setNewService(e.target.value)}
                  placeholder="e.g., Engine Rebuild, Detailing, etc."
                  className="input-field flex-1"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddService()
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddService}
                  className="btn-secondary px-4"
                >
                  Add
                </button>
              </div>

              {formData.services.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.services.map(service => (
                    <div
                      key={service}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-rdc-orange/8 rounded-full text-sm text-rdc-orange"
                    >
                      <span>{service}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveService(service)}
                        className="text-rdc-taupe hover:text-rdc-orange"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Location Card */}
        <div className="card p-8">
          <h2 className="section-title mb-6 flex items-center gap-2">
            <span className="text-lg">📍</span>
            Location
          </h2>

          <div className="space-y-6">
            <div>
              <label className="form-label">Business Location *</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleFieldChange}
                placeholder="City, State or Full Address"
                className="input-field"
              />
            </div>

            <div>
              <label className="form-label">Region</label>
              <select
                name="region"
                value={formData.region}
                onChange={handleFieldChange}
                className="input-field"
              >
                <option value="">Select a region</option>
                {REGIONS.map(region => (
                  <option key={region.id} value={region.id}>{region.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Photos Card */}
        <div className="card p-8">
          <h2 className="section-title mb-6 flex items-center gap-2">
            <span className="text-lg">📸</span>
            Photos ({photos.length}/6)
          </h2>

          <div className="space-y-6">
            {/* Logo Upload */}
            <div>
              <label className="form-label">Business Logo</label>
              <div className="flex items-center gap-4">
                {formData.logo_url ? (
                  <div className="relative">
                    <img
                      src={formData.logo_url}
                      alt="Logo"
                      className="w-24 h-24 rounded-lg object-cover border border-rdc-stone/30"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, logo_url: '' }))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <label className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                    <div className="w-24 h-24 rounded-lg border-2 border-dashed border-rdc-stone/50 bg-rdc-cream/50 flex items-center justify-center cursor-pointer hover:border-rdc-orange hover:bg-rdc-cream transition-colors">
                      {uploading ? (
                        <Loader size={20} className="animate-spin text-rdc-taupe" />
                      ) : (
                        <Upload size={20} className="text-rdc-taupe" />
                      )}
                    </div>
                  </label>
                )}
              </div>
            </div>

            {/* Photo Gallery */}
            <div>
              <label className="form-label mb-4">Gallery Photos</label>
              {photos.length < 6 && (
                <label className="block">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    disabled={uploading || photos.length >= 6}
                    className="hidden"
                  />
                  <div className="p-6 rounded-lg border-2 border-dashed border-rdc-stone/50 bg-rdc-cream/50 text-center cursor-pointer hover:border-rdc-orange hover:bg-rdc-cream transition-colors">
                    {uploading ? (
                      <>
                        <Loader size={24} className="animate-spin text-rdc-orange mx-auto mb-2" />
                        <p className="text-sm text-rdc-taupe">Uploading...</p>
                      </>
                    ) : (
                      <>
                        <Upload size={24} className="text-rdc-taupe mx-auto mb-2" />
                        <p className="text-sm text-rdc-charcoal font-medium">Click to upload photos</p>
                        <p className="text-xs text-rdc-taupe mt-1">
                          {6 - photos.length} slot{6 - photos.length !== 1 ? 's' : ''} remaining
                        </p>
                      </>
                    )}
                  </div>
                </label>
              )}

              {photos.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative">
                      <img
                        src={photo.url}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-32 rounded-lg object-cover border border-rdc-stone/30"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {saving && <Loader size={16} className="animate-spin" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/garage')}
            className="btn-secondary flex-1"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
